import { useFormik } from 'formik';
import React from 'react';
import { Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import * as yup from 'yup';
import {
  RewardBannerButtonAction,
  RewardBannerFormValues,
  RewardBannerPrizeType,
} from './types/RewardBannerTypes';
import { NumberField } from '../../../../ui/_fields/NumberField/NumberField';
import { TextareaFieldInput } from '../../../../ui/_fields/TextareaFieldInput/TextareaFieldInput';
import { TextField } from '../../../../ui/_fields/TextField/TextField';
import { FormSubmitRow, UnsavedChangesNotice } from '../../components/UnsavedChangesNotice/UnsavedChangesNotice';
import { useReportDirty } from '../../hooks/useReportDirty';
import { RewardBannerButtonTargetInput } from './components/RewardBannerButtonTargetInput';
import { RewardBannerCoverInput } from './components/RewardBannerCoverInput/RewardBannerCoverInput';

// eslint-disable-next-line no-template-curly-in-string
const requiredMessage = '${path} is required';
// eslint-disable-next-line no-template-curly-in-string
const maxMessage = '${path} should be up to ${max} characters';
// eslint-disable-next-line no-template-curly-in-string
const minMessage = '${path} should be at least ${min} characters';

const titleMaxLength = 50;
const descriptionMaxLength = 10000;
const labelMaxLength = 40;
const buttonTextMaxLength = 20;
const specialRequirementsMaxLength = 10000;
const maxPrizeValueLength = 50;

function buildSchema(promotionStoryUuid: string | null) {
  return yup.object().shape({
  title: yup.string().label('Title').max(titleMaxLength, maxMessage).required(requiredMessage),
  description: yup.string().label('Description').min(10, minMessage).max(descriptionMaxLength, maxMessage)
    .required(requiredMessage),
  label: yup.string().label('Label').max(labelMaxLength, maxMessage),
  imageUrl: yup.string().label('Image').required(requiredMessage),
  specialRequirements: yup.string().label('Rules').min(10, minMessage).max(specialRequirementsMaxLength, maxMessage),
  active: yup.bool(),
  priority: yup.number().label('Order').integer('Only whole numbers are allowed').min(1, minMessage)
    .required(requiredMessage),
  buttonText: yup.string().label('Button Text').max(buttonTextMaxLength, maxMessage).required(requiredMessage),
  buttonAction: yup.string().required(),
  buttonTarget: yup.string().when('buttonAction', {
    is: RewardBannerButtonAction.Story,
    then: (field) => field.test(
      'promotion-story-uuid',
      'No story UUID configured on this promotion',
      () => Boolean(promotionStoryUuid?.trim()),
    ),
    otherwise: (field) => field.label('Button Target').required(requiredMessage),
  }),
  prize: yup.object().shape({
    prizeType: yup.string(),
    prizeValue: yup.string(),
  }).test(
    'has-prize-value-if-hearts',
    "Prize value can't be empty if prize type selected",
    (prize) => (prize.prizeType === RewardBannerPrizeType.Hearts
      || prize.prizeType === RewardBannerPrizeType.Sapphires
      ? Boolean(prize.prizeValue)
      : true),
  ).test(
    'prize-value-is-too-long',
    `Prize Value should be up to ${maxPrizeValueLength}`,
    (prize) => {
      const length = prize.prizeValue?.length ?? 0;
      return length <= maxPrizeValueLength;
    },
  ),
  });
}

interface RewardBannerFormProps {
  formValues: RewardBannerFormValues;
  isLoading: boolean;
  onSubmit: (values: RewardBannerFormValues) => void;
  submitButtonText: string;
  disabled?: boolean;
  promotionStoryUuid: string | null;
  onDirtyChange?: (dirty: boolean) => void;
}

export function RewardBannerForm(props: RewardBannerFormProps) {
  const {
    formValues,
    isLoading,
    onSubmit,
    submitButtonText,
    disabled = false,
    promotionStoryUuid,
    onDirtyChange,
  } = props;

  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    setFieldValue,
    setValues,
    dirty,
  } = useFormik<RewardBannerFormValues>({
    enableReinitialize: true,
    validationSchema: buildSchema(promotionStoryUuid),
    initialValues: formValues,
    onSubmit: (submittedValues) => {
      onSubmit(submittedValues);
    },
  });

  useReportDirty(dirty, onDirtyChange);

  const handleImageChange = (fileInputValue: RewardBannerFormValues['newCoverImage'], dataURL: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      imageUrl: dataURL,
      newCoverImage: fileInputValue,
    }));
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <fieldset disabled={disabled}>
      <Row className="mb-3">
        <Col md={12}>
          <TextField
            label="Event Title"
            name="title"
            placeholder="COMPETITION STARTS!"
            value={values.title}
            onChange={handleChange}
            error={touched.title ? errors.title : ''}
            maxLength={titleMaxLength}
          />
        </Col>
        <Col md={12}>
          <TextareaFieldInput
            rows={3}
            label="Event Description"
            placeholder="Our Vampire Insiders will already be fans of Anne..."
            name="description"
            value={values.description}
            onChange={handleChange}
            error={touched.description ? errors.description : ''}
            maxLength={descriptionMaxLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <TextField
            label="Event Label"
            name="label"
            placeholder="Label to display on event card (for example FEATURED)"
            value={values.label}
            onChange={handleChange}
            error={touched.label ? errors.label : ''}
            maxLength={labelMaxLength}
          />
        </Col>
        <Col md={6}>
          <NumberField
            label="Order (bigger number - higher in the list)"
            name="priority"
            placeholder="order of events - events with bigger value shows first"
            min="1"
            step="1"
            value={values.priority}
            onChange={handleChange}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <TextField
            label="Button Text"
            name="buttonText"
            placeholder="Text to be displayed inside CTA button"
            value={values.buttonText}
            onChange={handleChange}
            error={touched.buttonText ? errors.buttonText : ''}
            maxLength={buttonTextMaxLength}
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Rules"
            name="specialRequirements"
            placeholder="Rules of event"
            value={values.specialRequirements}
            onChange={handleChange}
            error={touched.specialRequirements ? errors.specialRequirements : ''}
            maxLength={specialRequirementsMaxLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Button type</Form.Label>
            <Form.Control
              value={values.buttonAction}
              as="select"
              required
              name="buttonAction"
              onChange={handleChange}
              isInvalid={!!errors.buttonAction}
            >
              <option value={RewardBannerButtonAction.Story}>Story</option>
              <option value={RewardBannerButtonAction.Chat}>Chat</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <RewardBannerButtonTargetInput
            buttonAction={values.buttonAction}
            value={values.buttonTarget}
            onChange={(buttonTarget: string) => setFieldValue('buttonTarget', buttonTarget)}
            error={touched.buttonTarget ? errors.buttonTarget : ''}
            promotionStoryUuid={promotionStoryUuid}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Cover Prize Type</Form.Label>
            <Form.Control
              value={values.prize?.prizeType ?? ''}
              as="select"
              name="prize.prizeType"
              onChange={handleChange}
            >
              {Object.entries(RewardBannerPrizeType)
                .map(([key, value]) => (
                  <option key={key} value={value}>
                    {value !== RewardBannerPrizeType.None ? value : 'None'}
                  </option>
                ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <TextField
            label="Cover Prize value"
            name="prize.prizeValue"
            placeholder="Prize value - can be any text"
            value={values.prize?.prizeValue ?? ''}
            onChange={handleChange}
            error={touched.prize ? (errors.prize as string) : ''}
            maxLength={maxPrizeValueLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Form.Group as={Col} md="4">
          <Form.Check
            label="Active"
            name="active"
            checked={values.active}
            onChange={handleChange}
          />
        </Form.Group>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <RewardBannerCoverInput
            initialURL={values.imageUrl}
            onConfirm={handleImageChange}
            error={touched.imageUrl ? errors.imageUrl : ''}
            disabled={disabled}
            imageCropperProps={{
              aspectRatio: 21 / 10,
            }}
            getBlobOptions={{
              croppedImageType: 'image/jpeg',
              maxWidth: 3220,
              maxHeight: 1534,
              imageSmoothingQuality: 'high',
            }}
            accept="image/*"
          />
        </Col>
      </Row>
      </fieldset>

      {!disabled && (
        <FormSubmitRow>
          <Button type="submit" disabled={isLoading || !dirty}>
            {isLoading && (
              <Spinner
                size="sm"
                animation="border"
              />
            )}
            {submitButtonText}
          </Button>
          <UnsavedChangesNotice show={dirty} />
        </FormSubmitRow>
      )}
    </Form>
  );
}
